import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, Search, Upload, Plus, Trash2, Tag, X, Mail, Loader2, ListChecks,
  UserCheck, UserMinus, Copy, GitMerge, Lightbulb, Download, ChevronRight, MailX,
} from 'lucide-react';
import api from '../lib/api';
import { PageHeader } from '../components/layout/PageHeader';
import { EmptyState } from '../components/layout/EmptyState';
import { ImportContactsDialog } from '../components/contacts/ImportContactsDialog';
import { AddToListDialog } from '../components/contacts/ListDialogs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { downloadCsv } from '../lib/csv';

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selected, setSelected] = useState(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [sugOpen, setSugOpen] = useState(false);
  const [dupCount, setDupCount] = useState(0);
  const [sugCount, setSugCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (activeTag !== 'all') params.tag = activeTag;
      if (statusFilter !== 'all') params.status = statusFilter;
      const [c, t] = await Promise.all([
        api.get('/contacts', { params }),
        api.get('/contacts/tags/all'),
      ]);
      setContacts(c.data);
      setTags(t.data);
    } catch (e) {
      toast.error('Could not load contacts');
    } finally {
      setLoading(false);
    }
  }, [search, activeTag, statusFilter]);

  const loadMeta = useCallback(() => {
    api.get('/contacts/duplicates').then((r) => setDupCount(r.data.count)).catch(() => {});
    api.get('/contacts/inactive-suggestions').then((r) => setSugCount(r.data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const refreshAll = () => { load(); loadMeta(); };

  const allSelected = contacts.length > 0 && selected.size === contacts.length;
  const toggleAll = () => { allSelected ? setSelected(new Set()) : setSelected(new Set(contacts.map((c) => c.id))); };
  const toggleOne = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  const bulkDelete = async () => {
    try { await api.post('/contacts/bulk-delete', { ids: [...selected] }); toast.success(`Deleted ${selected.size} contacts`); setSelected(new Set()); refreshAll(); }
    catch (e) { toast.error('Delete failed'); }
  };
  const bulkStatus = async (status) => {
    try {
      await api.post('/contacts/bulk-status', { ids: [...selected], status });
      toast.success(status === 'inactive' ? 'Marked inactive' : 'Marked active');
      setSelected(new Set()); refreshAll();
    } catch (e) { toast.error('Could not update'); }
  };

  const [unsubOpen, setUnsubOpen] = useState(false);
  const bulkUnsubscribe = async () => {
    try {
      const n = selected.size;
      await api.post('/contacts/bulk-unsubscribe', { ids: [...selected] });
      toast.success(n === 1 ? 'Moved to your unsubscribe list' : `${n} contacts unsubscribed`);
      setSelected(new Set()); setUnsubOpen(false); refreshAll();
    } catch (e) { toast.error('Could not unsubscribe'); }
  };

  const exportContacts = () => {
    if (contacts.length === 0) { toast.error('No contacts to export'); return; }
    const headers = [
      { key: 'first_name', label: 'First name' }, { key: 'last_name', label: 'Last name' },
      { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' },
      { key: 'company', label: 'Company' }, { key: 'address', label: 'Address' },
      { key: 'dietary', label: 'Dietary/Allergies' },
      { key: 'tags', label: 'Tags' }, { key: 'status', label: 'Status' },
    ];
    const rows = contacts.map((c) => ({
      first_name: c.first_name || '', last_name: c.last_name || '',
      email: c.email || '', phone: c.phone || '',
      company: c.company || '', address: c.address || '', dietary: c.dietary || '',
      tags: (c.tags || []).join('; '), status: c.status || 'active',
    }));
    downloadCsv('thrive-contacts', headers, rows);
    toast.success(`Exported ${contacts.length} contacts`);
  };

  return (
    <div data-testid="contacts-page">
      <PageHeader title="Contacts" subtitle="Import, organize, and invite your people.">
        {dupCount > 0 && (
          <Button variant="secondary" onClick={() => setDupOpen(true)} className="gap-2" data-testid="duplicates-button">
            <GitMerge className="h-4 w-4" /> Duplicates <Badge variant="secondary" className="ml-1 text-accent">{dupCount}</Badge>
          </Button>
        )}
        <Button variant="secondary" onClick={exportContacts} className="gap-2" data-testid="contacts-export-button"><Download className="h-4 w-4" /> Export</Button>
        <Button variant="secondary" onClick={() => setAddOpen(true)} className="gap-2" data-testid="add-contact-button"><Plus className="h-4 w-4" /> Add</Button>
        <Button onClick={() => setImportOpen(true)} className="gap-2" data-testid="contacts-import-button"><Upload className="h-4 w-4" /> Import</Button>
      </PageHeader>

      {sugCount > 0 && (
        <div className="flex items-center gap-3 mb-4 p-4 rounded-xl bg-accent/10 border border-accent/20" data-testid="suggestions-banner">
          <Lightbulb className="h-5 w-5 text-accent flex-shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-medium">{sugCount} contact{sugCount !== 1 ? 's' : ''}</span> declined or never responded to past invites. Move them to inactive to keep your active list clean (you keep their info).
          </div>
          <Button size="sm" variant="secondary" onClick={() => setSugOpen(true)} data-testid="review-suggestions-button">Review</Button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email" className="h-11 pl-9" data-testid="contacts-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-11 w-full sm:w-40" data-testid="contacts-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
          </SelectContent>
        </Select>
        <Select value={activeTag} onValueChange={setActiveTag}>
          <SelectTrigger className="h-11 w-full sm:w-48" data-testid="contacts-tag-filter"><SelectValue placeholder="All tags" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tags</SelectItem>
            {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-4 p-3 rounded-xl bg-primary/5 border border-primary/15" data-testid="contacts-selection-bar">
          <span className="text-sm font-medium text-primary px-2">{selected.size} selected</span>
          <Button size="sm" onClick={() => setInviteOpen(true)} className="gap-1.5 h-9" data-testid="bulk-invite-button"><Mail className="h-3.5 w-3.5" /> Invite</Button>
          <Button size="sm" variant="secondary" onClick={() => setListOpen(true)} className="gap-1.5 h-9" data-testid="bulk-addlist-button"><ListChecks className="h-3.5 w-3.5" /> Add to list</Button>
          <Button size="sm" variant="secondary" onClick={() => setTagOpen(true)} className="gap-1.5 h-9" data-testid="bulk-tag-button"><Tag className="h-3.5 w-3.5" /> Tag</Button>
          <Button size="sm" variant="ghost" onClick={() => bulkStatus('inactive')} className="gap-1.5 h-9" data-testid="bulk-inactive-button"><UserMinus className="h-3.5 w-3.5" /> Mark inactive</Button>
          <Button size="sm" variant="ghost" onClick={() => bulkStatus('active')} className="gap-1.5 h-9" data-testid="bulk-active-button"><UserCheck className="h-3.5 w-3.5" /> Mark active</Button>
          <Button size="sm" variant="ghost" onClick={() => setUnsubOpen(true)} className="gap-1.5 h-9 text-accent" data-testid="bulk-unsubscribe-button"><MailX className="h-3.5 w-3.5" /> Unsubscribe</Button>
          <Button size="sm" variant="ghost" onClick={bulkDelete} className="gap-1.5 h-9 text-destructive" data-testid="bulk-delete-button"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="gap-1 h-9 ml-auto"><X className="h-3.5 w-3.5" /> Clear</Button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]">
        {loading ? (
          <div className="p-4 space-y-2">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
        ) : contacts.length === 0 ? (
          <EmptyState icon={Users} title="No contacts here"
            description={search || activeTag !== 'all' || statusFilter !== 'all' ? 'No contacts match your filters.' : 'Import your contact list to start inviting people to your events.'}
            action={<Button onClick={() => setImportOpen(true)} className="gap-2"><Upload className="h-4 w-4" /> Import contacts</Button>} />
        ) : (
          <table className="w-full" data-testid="contacts-table">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="w-12 px-4 py-3"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="contacts-select-all-checkbox" /></th>
                <th className="text-left px-2 py-3 text-sm font-medium text-muted-foreground">Name</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground hidden md:table-cell">Tags</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground hidden sm:table-cell">Status</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} className={`border-b border-border last:border-0 transition-colors ${selected.has(c.id) ? 'bg-primary/5' : 'hover:bg-secondary/40'}`} data-testid="contact-row">
                  <td className="px-4 py-3"><Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} data-testid="contact-checkbox" /></td>
                  <td className="px-2 py-3">
                    <button
                      onClick={() => navigate(`/app/contacts/${c.id}`)}
                      className="font-medium text-sm text-foreground hover:text-primary transition-colors text-left inline-flex items-center gap-1 group"
                      data-testid="contact-name-link"
                    >
                      {`${c.first_name} ${c.last_name}`.trim() || '—'}
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                    <div className="text-xs text-muted-foreground sm:hidden">{c.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">{c.email}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="flex gap-1 flex-wrap">{(c.tags || []).map((t) => <Badge key={t} variant="secondary" className="font-normal">{t}</Badge>)}</div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {c.status === 'inactive'
                      ? <Badge variant="secondary" className="text-muted-foreground">Inactive</Badge>
                      : <Badge variant="secondary" className="text-primary">Active</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!loading && contacts.length > 0 && <p className="text-sm text-muted-foreground mt-3">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</p>}

      <ImportContactsDialog open={importOpen} onOpenChange={setImportOpen} onImported={refreshAll} />
      <AddContactDialog open={addOpen} onOpenChange={setAddOpen} onAdded={refreshAll} />
      <BulkTagDialog open={tagOpen} onOpenChange={setTagOpen} ids={[...selected]} onDone={() => { setSelected(new Set()); load(); }} />
      <InviteToEventDialog open={inviteOpen} onOpenChange={setInviteOpen} contactIds={[...selected]} />
      <AddToListDialog open={listOpen} onOpenChange={setListOpen} contactIds={[...selected]} onDone={() => setSelected(new Set())} />
      <DuplicatesDialog open={dupOpen} onOpenChange={setDupOpen} onMerged={refreshAll} />
      <SuggestionsDialog open={sugOpen} onOpenChange={setSugOpen} onDone={refreshAll} />

      <AlertDialog open={unsubOpen} onOpenChange={setUnsubOpen}>
        <AlertDialogContent data-testid="contacts-unsubscribe-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsubscribe {selected.size} contact{selected.size !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              They&apos;ll be moved to your Unsubscribed list and won&apos;t receive any event emails. You can restore them anytime — their details are kept safe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="contacts-unsubscribe-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkUnsubscribe} className="gap-1.5" data-testid="contacts-unsubscribe-confirm">
              <MailX className="h-4 w-4" /> Unsubscribe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const AddContactDialog = ({ open, onOpenChange, onAdded }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', company: '', address: '', dietary: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/contacts', form);
      toast.success('Contact added');
      setForm({ first_name: '', last_name: '', email: '', phone: '', company: '', address: '', dietary: '' });
      onAdded(); onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not add contact'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-contact-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add contact</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-11" data-testid="contact-firstname-input" /></div>
            <div className="space-y-1.5"><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="h-11" /></div>
          </div>
          <div className="space-y-1.5"><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="contact-email-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" /></div>
            <div className="space-y-1.5"><Label>Company / business</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="h-11" data-testid="contact-company-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11" data-testid="contact-address-input" /></div>
          <div className="space-y-1.5"><Label>Dietary / allergies</Label><Input value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })} className="h-11" data-testid="contact-dietary-input" /></div>
          <DialogFooter><Button type="submit" disabled={saving} className="w-full h-11" data-testid="save-contact-button">{saving ? 'Saving…' : 'Add contact'}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const BulkTagDialog = ({ open, onOpenChange, ids, onDone }) => {
  const [tag, setTag] = useState('');
  const apply = async () => {
    if (!tag.trim()) { toast.error('Enter a tag'); return; }
    try { await api.post('/contacts/bulk-tag', { ids, tag: tag.trim() }); toast.success('Tag added'); setTag(''); onDone(); onOpenChange(false); }
    catch (e) { toast.error('Could not add tag'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-2xl">Tag {ids.length} contacts</DialogTitle></DialogHeader>
        <div className="space-y-1.5 mt-2"><Label>Tag name</Label><Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. VIP" className="h-11" data-testid="bulk-tag-input" /></div>
        <DialogFooter><Button onClick={apply} className="w-full h-11" data-testid="apply-tag-button">Apply tag</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InviteToEventDialog = ({ open, onOpenChange, contactIds }) => {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => { if (open) api.get('/events').then((r) => setEvents(r.data)).catch(() => {}); }, [open]);
  const send = async () => {
    if (!eventId) { toast.error('Pick an event'); return; }
    setSending(true);
    try {
      const res = await api.post(`/events/${eventId}/send`, { contact_ids: contactIds });
      toast.success(`Sent ${res.data.sent} invites${res.data.failed ? `, ${res.data.failed} failed` : ''}`);
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send invites'); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invite-to-event-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Invite {contactIds.length} to an event</DialogTitle></DialogHeader>
        <div className="space-y-1.5 mt-2">
          <Label>Choose event</Label>
          <Select value={eventId} onValueChange={setEventId}>
            <SelectTrigger className="h-11" data-testid="invite-event-select"><SelectValue placeholder="Select an event" /></SelectTrigger>
            <SelectContent>{events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.title}</SelectItem>)}</SelectContent>
          </Select>
          {events.length === 0 && <p className="text-sm text-muted-foreground">Create an event first.</p>}
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={sending || !eventId} className="w-full h-11 gap-2" data-testid="confirm-invite-button">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}{sending ? 'Sending…' : 'Send invites'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DuplicatesDialog = ({ open, onOpenChange, onMerged }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [primary, setPrimary] = useState({}); // groupIdx -> contactId
  const [merging, setMerging] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/contacts/duplicates').then((r) => {
      setGroups(r.data.groups);
      const p = {}; r.data.groups.forEach((g, i) => { p[i] = g.contacts[0].id; });
      setPrimary(p);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) { const t = setTimeout(load, 0); return () => clearTimeout(t); } return undefined; }, [open, load]);

  const merge = async (gi) => {
    const g = groups[gi];
    const primaryId = primary[gi];
    const dupIds = g.contacts.filter((c) => c.id !== primaryId).map((c) => c.id);
    setMerging(gi);
    try {
      await api.post('/contacts/merge', { primary_id: primaryId, duplicate_ids: dupIds });
      toast.success('Contacts merged');
      onMerged && onMerged();
      load();
    } catch (e) { toast.error('Could not merge'); }
    finally { setMerging(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="duplicates-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Review duplicates</DialogTitle></DialogHeader>
        {loading ? <Skeleton className="h-40 rounded-xl" /> :
          groups.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No duplicates found. Your contacts are clean!</p> : (
            <div className="space-y-4 max-h-[60vh] overflow-auto">
              {groups.map((g, gi) => (
                <div key={gi} className="rounded-xl border border-border p-4" data-testid="duplicate-group">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="secondary" className="text-accent capitalize">{g.reason}</Badge>
                    <Button size="sm" onClick={() => merge(gi)} disabled={merging === gi} className="gap-1.5 h-9" data-testid="merge-button">
                      {merging === gi ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitMerge className="h-3.5 w-3.5" />} Merge
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Choose which record to keep — the others merge into it (tags combined, then deleted).</p>
                  <div className="space-y-2">
                    {g.contacts.map((c) => (
                      <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer ${primary[gi] === c.id ? 'border-primary bg-primary/5' : 'border-border'}`}>
                        <input type="radio" name={`primary-${gi}`} checked={primary[gi] === c.id} onChange={() => setPrimary({ ...primary, [gi]: c.id })} className="accent-[color:var(--primary)]" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium">{`${c.first_name} ${c.last_name}`.trim() || '—'}</div>
                          <div className="text-xs text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ''}</div>
                        </div>
                        {primary[gi] === c.id && <span className="text-xs font-medium text-primary">Keep</span>}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
};

const SuggestionsDialog = ({ open, onOpenChange, onDone }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/contacts/inactive-suggestions').then((r) => setSuggestions(r.data.suggestions)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (open) { const t = setTimeout(load, 0); return () => clearTimeout(t); } return undefined; }, [open, load]);

  const markInactive = async (ids) => {
    setBusy(true);
    try {
      await api.post('/contacts/bulk-status', { ids, status: 'inactive' });
      toast.success(`Moved ${ids.length} to inactive`);
      onDone && onDone();
      load();
    } catch (e) { toast.error('Could not update'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="suggestions-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Inactive suggestions</DialogTitle></DialogHeader>
        {loading ? <Skeleton className="h-40 rounded-xl" /> :
          suggestions.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Nothing to suggest right now.</p> : (
            <>
              <div className="flex justify-end mb-2">
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => markInactive(suggestions.map((s) => s.contact.id))} data-testid="mark-all-inactive">Mark all inactive</Button>
              </div>
              <div className="space-y-2 max-h-[55vh] overflow-auto">
                {suggestions.map((s) => (
                  <div key={s.contact.id} className="flex items-center gap-3 p-3 rounded-xl border border-border" data-testid="suggestion-row">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{`${s.contact.first_name} ${s.contact.last_name}`.trim() || s.contact.email}</div>
                      <div className="text-xs text-muted-foreground">{s.contact.email}</div>
                      <Badge variant="secondary" className="mt-1 font-normal text-muted-foreground">{s.reason}</Badge>
                    </div>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => markInactive([s.contact.id])} className="gap-1.5" data-testid="mark-one-inactive"><UserMinus className="h-3.5 w-3.5" /> Inactive</Button>
                  </div>
                ))}
              </div>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
};
